/**
 * Application shell — header, navigation, live status.
 *
 * The header carries the one thing an operator needs at a glance even when they
 * are on another screen: whether anything is running right now. Burying that in
 * the runs tab means starting a second regression by accident.
 *
 * REAL LINKS to real paths (`<Link>`, `useLocation`), not a `tab` prop the
 * parent held in memory — see App.tsx's header comment for why that broke
 * Back/Forward and refresh. The active tab is derived from the current URL,
 * never passed in, so it can never drift from what is actually on screen.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { RunSummary } from '../api';

interface NavTab {
  path: string;
  label: string;
  hint: string;
}

// Recipes and client recordings are reached FROM the Tests tab now (its own
// start cards), not as separate top-level tabs — they are two more ways to
// arrive at a test, not a different kind of page. `/recipes` and
// `/client-recordings` are still real, directly linkable routes (App.tsx),
// just not competing with Tests for a permanent spot in this nav bar.
const TABS: NavTab[] = [
  { path: '/runs', label: 'Runs', hint: 'Start a run and watch it' },
  { path: '/tests', label: 'Tests', hint: 'Every test — however it was created — in one place' },
  // NOT `/health` — see App.tsx's comment on that route: it collides with
  // the Docker healthcheck endpoint `server.ts` always serves at that exact
  // path.
  { path: '/test-health', label: 'Health', hint: 'Flaky, stale, always-passing and high-value-failing tests' },
  { path: '/reports', label: 'Reports', hint: 'History, failures and trends' },
  { path: '/tracker', label: 'Tracker', hint: 'Bugs and enhancements QA has raised' },
  { path: '/extras', label: 'Extras', hint: 'Tier 1 categories and tests that can\'t be automated' },
];

interface Props {
  user?: { email: string; role: string };
  active: RunSummary[];
  onSignOut: () => void;
  children: ReactNode;
}

export function Shell({ user, active, onSignOut, children }: Props) {
  const running = active.filter((r) => r.status === 'running');
  const queued = active.filter((r) => r.status === 'queued');
  const location = useLocation();

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-20 backdrop-blur"
        style={{ background: 'hsl(var(--bg) / 0.85)', borderBottom: '1px solid hsl(var(--border))' }}
      >
        <div className="mx-auto flex max-w-[1400px] items-center gap-6 px-6 py-3">
          <div className="flex items-center gap-2.5">
            <img src="/svg/logo-dark.svg" alt="" className="h-7 w-auto" />
            <span className="text-sm font-semibold tracking-tight">QA Console</span>
          </div>

          <nav className="flex items-center gap-1">
            {TABS.map((t) => {
              // /runs/:runId is still "on" the Runs tab — a prefix match, not
              // an exact one, or opening a specific run would show no tab as
              // active at all.
              const isActive = location.pathname === t.path || location.pathname.startsWith(`${t.path}/`);
              return (
                <Link
                  key={t.path}
                  to={t.path}
                  title={t.hint}
                  data-testid={`tab-${t.path.slice(1)}`}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium transition"
                  style={
                    isActive
                      ? { background: 'hsl(var(--bg-overlay))', color: 'hsl(var(--text))' }
                      : { color: 'hsl(var(--text-muted))' }
                  }
                >
                  {t.label}
                </Link>
              );
            })}
          </nav>

          {/* Visible from every screen — see the note at the top of this file. */}
          {(running.length > 0 || queued.length > 0) && (
            <Link to="/runs" className="pill pill-accent" data-testid="active-runs-indicator" title="Go to runs">
              <span className="dot dot-live" />
              {running.length > 0 && `${running.length} running`}
              {running.length > 0 && queued.length > 0 && ' · '}
              {queued.length > 0 && `${queued.length} queued`}
            </Link>
          )}

          <div className="ml-auto flex items-center gap-3">
            {user && (
              <span className="hidden text-xs sm:block" style={{ color: 'hsl(var(--text-faint))' }}>
                {user.email}
              </span>
            )}
            <button onClick={onSignOut} className="btn btn-ghost text-xs" data-testid="sign-out">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-6">{children}</main>
    </div>
  );
}
